import { Popover } from "@medusajs/ui"
import { useEffect, useMemo, useState } from "react"
import { useMe } from "@/lib/api"
import type { CompareSpec, RangeSpec } from "./api"
import { rangeLabel } from "./format"
import { addDays, blackFriday, rangeDates, todayLocal, ymd, type Ymd } from "./time"

/** Store time zone (Settings → General), which every range is computed in. */
export function useStoreTz() {
  const { data } = useMe()
  return (data as any)?.store?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York"
}

const chip =
  "inline-flex h-8 items-center gap-1.5 rounded-lg border border-[#d4d4d4] bg-white px-2.5 text-[13px] font-medium text-[#303030] shadow-[0_1px_0_rgba(0,0,0,0.05)] hover:bg-[#f7f7f7] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#005bd3]"
const Cal = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" aria-hidden="true">
    <path fill="currentColor" d="M6 2.75a.75.75 0 0 1 1.5 0V4h5V2.75a.75.75 0 0 1 1.5 0V4h.25A2.75 2.75 0 0 1 17 6.75v7.5A2.75 2.75 0 0 1 14.25 17h-8.5A2.75 2.75 0 0 1 3 14.25v-7.5A2.75 2.75 0 0 1 5.75 4H6V2.75ZM4.5 8.5v5.75c0 .69.56 1.25 1.25 1.25h8.5c.69 0 1.25-.56 1.25-1.25V8.5h-11Z" />
  </svg>
)
const Chevron = ({ open }: { open?: boolean }) => (
  <svg width="12" height="12" viewBox="0 0 20 20" aria-hidden="true" className={open ? "rotate-180" : ""}>
    <path fill="currentColor" d="M5.72 8.47a.75.75 0 0 1 1.06 0L10 11.69l3.22-3.22a.75.75 0 1 1 1.06 1.06l-3.75 3.75a.75.75 0 0 1-1.06 0L5.72 9.53a.75.75 0 0 1 0-1.06Z" />
  </svg>
)

type Mode = "today" | "yesterday" | "last" | "to_date" | "black_friday" | "cyber_monday" | "quarters" | "custom"
const MODE_OF = (p?: string): Mode => {
  if (!p || p === "custom") return "custom"
  if (p === "today" || p === "yesterday") return p
  if (/^last_\d+_(days|weeks|months)/.test(p)) return "last"
  if (/_to_date$/.test(p)) return "to_date"
  if (p.startsWith("black_friday")) return "black_friday"
  if (p.startsWith("cyber_monday")) return "cyber_monday"
  if (/^q[1-4]_/.test(p)) return "quarters"
  return "custom"
}

/** Short chip text for a range ("Today", "Last 30 days", "Oct 1–25, 2026"). */
export function rangeChipLabel(r: RangeSpec, tz: string) {
  const d = rangeDates(r, tz)
  if (!r.preset || r.preset === "custom") return rangeLabel(ymd(d.from), ymd(d.to))
  return d.label
}

/**
 * Shopify's date picker: presets on the left (Today, Yesterday, Last N days /
 * weeks / months with "Include today", Period to date, Black Friday, Cyber
 * Monday, Quarters, Custom range), two months of calendar, Cancel / Apply.
 */
export function DateRangePicker({ value, onChange }: { value: RangeSpec; onChange: (r: RangeSpec) => void }) {
  const tz = useStoreTz()
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<RangeSpec>(value)
  const [mode, setMode] = useState<Mode>(MODE_OF(value.preset))
  const [lastN, setLastN] = useState(() => {
    const m = /^last_(\d+)_(days|weeks|months)(_incl)?$/.exec(value.preset ?? "")
    return { n: m ? Number(m[1]) : 7, unit: (m?.[2] ?? "days") as "days" | "weeks" | "months", incl: !!m?.[3] || !m }
  })
  const [anchor, setAnchor] = useState<string | null>(null)
  useEffect(() => {
    if (open) {
      setDraft(value)
      setMode(MODE_OF(value.preset))
      setAnchor(null)
    }
  }, [open, value])
  const today = todayLocal(tz)
  const sel = rangeDates(draft, tz)
  const [view, setView] = useState<Ymd>({ y: today.y, m: today.m, d: 1 })
  // Right-hand month = the month the range ends in (Shopify), so the left one is the month before.
  useEffect(() => {
    if (open) setView(sel.to.m === 1 ? { y: sel.to.y - 1, m: 12, d: 1 } : { y: sel.to.y, m: sel.to.m - 1, d: 1 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const pickMode = (m: Mode) => {
    setMode(m)
    if (m === "today" || m === "yesterday") setDraft({ preset: m })
    if (m === "last") setDraft({ preset: `last_${lastN.n}_${lastN.unit}${lastN.incl ? "_incl" : ""}` })
    if (m === "to_date") setDraft({ preset: "month_to_date" })
    if (m === "black_friday") setDraft({ preset: `black_friday_${blackFriday(today.y).m * 100 + blackFriday(today.y).d <= today.m * 100 + today.d ? today.y : today.y - 1}` })
    if (m === "cyber_monday") setDraft({ preset: `cyber_monday_${today.m === 12 || (today.m === 11 && addDays(blackFriday(today.y), 3).d <= today.d) ? today.y : today.y - 1}` })
    if (m === "quarters") setDraft({ preset: `q${Math.floor((today.m - 1) / 3) + 1}_${today.y}` })
    if (m === "custom") setDraft({ preset: "custom", since: ymd(sel.from), until: ymd(sel.to) })
  }
  const setLast = (p: Partial<typeof lastN>) => {
    const v = { ...lastN, ...p }
    setLastN(v)
    setDraft({ preset: `last_${Math.min(Math.max(v.n || 1, 1), 999)}_${v.unit}${v.incl ? "_incl" : ""}` })
  }
  const clickDay = (d: string) => {
    setMode("custom")
    if (!anchor) {
      setAnchor(d)
      setDraft({ preset: "custom", since: d, until: d })
    } else {
      const [a, b] = anchor <= d ? [anchor, d] : [d, anchor]
      setDraft({ preset: "custom", since: a, until: b })
      setAnchor(null)
    }
  }
  const quarters = useMemo(() => {
    const out: string[] = []
    let y = today.y
    let q = Math.floor((today.m - 1) / 3) + 1
    for (let i = 0; i < 8; i++) {
      out.push(`q${q}_${y}`)
      q--
      if (!q) (q = 4), y--
    }
    return out
  }, [today.y, today.m])
  const years = [today.y, today.y - 1, today.y - 2]
  const left: [Mode, string][][] = [
    [
      ["today", "Today"],
      ["yesterday", "Yesterday"],
    ],
    [
      ["last", "Last"],
      ["to_date", "Period to date"],
    ],
    [
      ["black_friday", "Black Friday"],
      ["cyber_monday", "Cyber Monday"],
      ["quarters", "Quarters"],
    ],
    [["custom", "Custom range"]],
  ]
  const nextMonth = { y: view.m === 12 ? view.y + 1 : view.y, m: view.m === 12 ? 1 : view.m + 1, d: 1 }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button type="button" className={chip} aria-label={`Date range: ${rangeChipLabel(value, tz)}`}>
          <Cal />
          {rangeChipLabel(value, tz)}
          <Chevron open={open} />
        </button>
      </Popover.Trigger>
      <Popover.Content align="start" className="z-[70] w-[min(700px,calc(100vw-24px))] p-0">
        <div className="flex max-h-[80vh] flex-col overflow-auto sm:flex-row">
          <nav aria-label="Date presets" className="flex shrink-0 flex-row flex-wrap gap-1 border-b border-[#ebebeb] p-2 sm:w-[150px] sm:flex-col sm:border-b-0 sm:border-r">
            {left.map((group, gi) => (
              <div key={gi} className={`flex flex-row flex-wrap gap-1 sm:flex-col ${gi ? "sm:border-t sm:border-[#ebebeb] sm:pt-1" : ""}`}>
                {group.map(([m, label]) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => pickMode(m)}
                    aria-pressed={mode === m}
                    className={`rounded-md px-2 py-1.5 text-left text-[13px] ${mode === m ? "bg-[#ebebeb] font-medium" : "hover:bg-[#f3f3f3]"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            ))}
          </nav>
          <div className="flex-1 p-3">
            {mode === "last" && (
              <div className="mb-3 flex flex-wrap items-center gap-2 text-[13px]">
                <span>Last</span>
                <input aria-label="Number" type="number" min={1} max={999} value={lastN.n} onChange={(e) => setLast({ n: Number(e.target.value) })} className="h-8 w-20 rounded-lg border border-[#8a8a8a] px-2" />
                <select aria-label="Unit" value={lastN.unit} onChange={(e) => setLast({ unit: e.target.value as any })} className="h-8 rounded-lg border border-[#8a8a8a] px-2">
                  <option value="days">Days</option>
                  <option value="weeks">Weeks</option>
                  <option value="months">Months</option>
                </select>
                <label className="flex items-center gap-1.5">
                  <input type="checkbox" checked={lastN.incl} onChange={(e) => setLast({ incl: e.target.checked })} /> Include today
                </label>
              </div>
            )}
            {mode === "to_date" && (
              <div className="mb-3 flex flex-wrap gap-1">
                {[
                  ["week_to_date", "Week to date"],
                  ["month_to_date", "Month to date"],
                  ["quarter_to_date", "Quarter to date"],
                  ["year_to_date", "Year to date"],
                ].map(([p, l]) => (
                  <Pill key={p} active={draft.preset === p} onClick={() => setDraft({ preset: p })}>{l}</Pill>
                ))}
              </div>
            )}
            {(mode === "black_friday" || mode === "cyber_monday") && (
              <div className="mb-3 flex flex-wrap gap-1">
                {years.map((y) => (
                  <Pill key={y} active={draft.preset === `${mode}_${y}`} onClick={() => setDraft({ preset: `${mode}_${y}` })}>{`${mode === "black_friday" ? "Black Friday" : "Cyber Monday"} ${y}`}</Pill>
                ))}
                {years.map((y) => (
                  <Pill key={`b${y}`} active={draft.preset === `bfcm_${y}`} onClick={() => setDraft({ preset: `bfcm_${y}` })}>{`BFCM ${y}`}</Pill>
                ))}
              </div>
            )}
            {mode === "quarters" && (
              <div className="mb-3 flex flex-wrap gap-1">
                {quarters.map((q) => (
                  <Pill key={q} active={draft.preset === q} onClick={() => setDraft({ preset: q })}>{q.replace(/^q(\d)_(\d+)$/, "Q$1 $2")}</Pill>
                ))}
              </div>
            )}
            <div className="flex items-start gap-4">
              <Month month={view} from={ymd(sel.from)} to={ymd(sel.to)} today={ymd(today)} onDay={clickDay} onPrev={() => setView({ y: view.m === 1 ? view.y - 1 : view.y, m: view.m === 1 ? 12 : view.m - 1, d: 1 })} />
              <div className="hidden sm:block">
                <Month month={nextMonth} from={ymd(sel.from)} to={ymd(sel.to)} today={ymd(today)} onDay={clickDay} onNext={() => setView(nextMonth)} />
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-[#ebebeb] pt-3">
              <span className="text-[13px] text-ui-fg-subtle">{rangeLabel(ymd(sel.from), ymd(sel.to))}</span>
              <div className="flex gap-2">
                <button type="button" className={chip} onClick={() => setOpen(false)}>Cancel</button>
                <button
                  type="button"
                  className="inline-flex h-8 items-center rounded-lg bg-[#303030] px-3 text-[13px] font-medium text-white hover:bg-[#1a1a1a] disabled:opacity-50"
                  disabled={!!anchor}
                  onClick={() => {
                    onChange(draft)
                    setOpen(false)
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      </Popover.Content>
    </Popover>
  )
}

function Pill({ active, onClick, children }: { active: boolean; onClick: () => void; children: string }) {
  return (
    <button type="button" aria-pressed={active} onClick={onClick} className={`rounded-full border px-2.5 py-1 text-[12px] ${active ? "border-[#303030] bg-[#303030] text-white" : "border-[#d4d4d4] hover:bg-[#f3f3f3]"}`}>
      {children}
    </button>
  )
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
function Month({ month, from, to, today, onDay, onPrev, onNext }: { month: Ymd; from: string; to: string; today: string; onDay: (d: string) => void; onPrev?: () => void; onNext?: () => void }) {
  const first = new Date(Date.UTC(month.y, month.m - 1, 1)).getUTCDay()
  const days = new Date(Date.UTC(month.y, month.m, 0)).getUTCDate()
  const cells: (string | null)[] = [...Array(first).fill(null), ...Array.from({ length: days }, (_, i) => ymd({ y: month.y, m: month.m, d: i + 1 }))]
  return (
    <div className="w-[238px]">
      <div className="mb-2 flex items-center justify-between">
        <button type="button" aria-label="Previous month" onClick={onPrev} className={`h-7 w-7 rounded-md hover:bg-[#f3f3f3] ${onPrev ? "" : "invisible"}`}>‹</button>
        <span className="text-[13px] font-semibold">{MONTHS[month.m - 1]} {month.y}</span>
        <button type="button" aria-label="Next month" onClick={onNext} className={`h-7 w-7 rounded-md hover:bg-[#f3f3f3] ${onNext ? "" : "invisible"}`}>›</button>
      </div>
      <div className="grid grid-cols-7 text-center text-[11px] text-ui-fg-subtle">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <span key={d} className="py-1">{d}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 text-center text-[12px]">
        {cells.map((c, i) => {
          if (!c) return <span key={i} />
          const inRange = c >= from && c <= to
          const edge = c === from || c === to
          const future = c > today
          return (
            <button
              key={c}
              type="button"
              disabled={future}
              onClick={() => onDay(c)}
              aria-label={c}
              aria-pressed={inRange}
              className={`h-8 ${edge ? "rounded-md bg-[#303030] font-semibold text-white" : inRange ? "bg-[#ebebeb]" : "rounded-md hover:bg-[#f3f3f3]"} ${future ? "text-[#cccccc]" : ""} ${c === today && !edge ? "font-semibold" : ""}`}
            >
              {Number(c.slice(8))}
            </button>
          )
        })}
      </div>
    </div>
  )
}

const COMPARE: [Exclude<CompareSpec, object>, string][] = [
  ["none", "No comparison"],
  ["previous_period", "Previous period"],
  ["previous_year", "Previous year"],
  ["previous_year_dow", "Previous year (match day of week)"],
]

/** Shopify's comparison menu. "Custom" opens the date picker; "Targets" switches cards to target progress. */
export function ComparePicker({ value, onChange, targets, onTargets }: { value: CompareSpec; onChange: (c: CompareSpec) => void; targets?: boolean; onTargets?: (on: boolean) => void }) {
  const [open, setOpen] = useState(false)
  const [custom, setCustom] = useState(false)
  const label = targets ? "Targets" : typeof value === "object" ? `Compare: ${rangeLabel(value.since, value.until)}` : (COMPARE.find(([v]) => v === value)?.[1] ?? "No comparison")
  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button type="button" className={chip} aria-label={`Comparison: ${label}`}>
            <svg width="16" height="16" viewBox="0 0 20 20" aria-hidden="true"><path fill="currentColor" d="M3 5.75A.75.75 0 0 1 3.75 5h9.69l-1.72-1.72a.75.75 0 0 1 1.06-1.06l3 3a.75.75 0 0 1 0 1.06l-3 3a.75.75 0 1 1-1.06-1.06l1.72-1.72H3.75A.75.75 0 0 1 3 5.75Zm14 8.5a.75.75 0 0 1-.75.75H6.56l1.72 1.72a.75.75 0 1 1-1.06 1.06l-3-3a.75.75 0 0 1 0-1.06l3-3a.75.75 0 1 1 1.06 1.06L6.56 13.5h9.69a.75.75 0 0 1 .75.75Z" /></svg>
            {label}
            <Chevron open={open} />
          </button>
        </Popover.Trigger>
        <Popover.Content align="start" className="z-[70] w-[260px] p-1">
          <ul role="menu" className="flex flex-col">
            {COMPARE.map(([v, l]) => (
              <li key={v}>
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={!targets && value === v}
                  onClick={() => {
                    onTargets?.(false)
                    onChange(v)
                    setOpen(false)
                  }}
                  className={`w-full rounded-md px-2 py-1.5 text-left text-[13px] ${!targets && value === v ? "bg-[#ebebeb] font-medium" : "hover:bg-[#f3f3f3]"}`}
                >
                  {l}
                </button>
              </li>
            ))}
            <li>
              <button type="button" role="menuitemradio" aria-checked={!targets && typeof value === "object"} onClick={() => (setOpen(false), setCustom(true))} className={`w-full rounded-md px-2 py-1.5 text-left text-[13px] ${!targets && typeof value === "object" ? "bg-[#ebebeb] font-medium" : "hover:bg-[#f3f3f3]"}`}>
                Custom
              </button>
            </li>
            {onTargets && (
              <li>
                <button type="button" role="menuitemradio" aria-checked={!!targets} onClick={() => (onTargets(true), setOpen(false))} className={`w-full rounded-md px-2 py-1.5 text-left text-[13px] ${targets ? "bg-[#ebebeb] font-medium" : "hover:bg-[#f3f3f3]"}`}>
                  Targets
                </button>
              </li>
            )}
          </ul>
        </Popover.Content>
      </Popover>
      {custom && (
        <CustomCompare
          onClose={() => setCustom(false)}
          onApply={(r) => {
            onTargets?.(false)
            onChange(r)
            setCustom(false)
          }}
        />
      )}
    </>
  )
}

function CustomCompare({ onClose, onApply }: { onClose: () => void; onApply: (r: { since: string; until: string }) => void }) {
  const tz = useStoreTz()
  const t = todayLocal(tz)
  const [since, setSince] = useState(ymd(addDays(t, -60)))
  const [until, setUntil] = useState(ymd(addDays(t, -31)))
  return (
    <div role="dialog" aria-label="Custom comparison" className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div className="w-full max-w-sm rounded-xl bg-white p-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="mb-3 text-[14px] font-semibold">Compare to a custom range</h2>
        <div className="grid grid-cols-2 gap-2 text-[13px]">
          <label>Start date<input type="date" value={since} max={until} onChange={(e) => setSince(e.target.value)} className="mt-1 h-8 w-full rounded-lg border border-[#8a8a8a] px-2" /></label>
          <label>End date<input type="date" value={until} min={since} max={ymd(t)} onChange={(e) => setUntil(e.target.value)} className="mt-1 h-8 w-full rounded-lg border border-[#8a8a8a] px-2" /></label>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className={chip} onClick={onClose}>Cancel</button>
          <button type="button" className="h-8 rounded-lg bg-[#303030] px-3 text-[13px] font-medium text-white" onClick={() => onApply({ since, until })}>Apply</button>
        </div>
      </div>
    </div>
  )
}

/** Currency chip (USD, locked like the store currency). */
export function CurrencyChip() {
  return (
    <span className={chip + " cursor-default"} title="All amounts are in the store currency">
      <svg width="16" height="16" viewBox="0 0 20 20" aria-hidden="true"><path fill="currentColor" d="M10 2a8 8 0 1 0 0 16 8 8 0 0 0 0-16Zm.75 3.5v.6c1.06.2 1.94.86 2.17 1.85a.75.75 0 1 1-1.46.35c-.1-.42-.62-.8-1.46-.8-.93 0-1.46.45-1.46.88 0 .37.2.7 1.6.98 1.7.34 2.86.96 2.86 2.39 0 1.06-.85 1.92-2.25 2.15v.6a.75.75 0 0 1-1.5 0v-.6c-1.06-.2-1.94-.86-2.17-1.85a.75.75 0 1 1 1.46-.35c.1.42.62.8 1.46.8.93 0 1.46-.45 1.46-.88 0-.37-.2-.7-1.6-.98-1.7-.34-2.86-.96-2.86-2.39 0-1.06.85-1.92 2.25-2.15v-.6a.75.75 0 0 1 1.5 0Z" /></svg>
      USD $
    </span>
  )
}

export const chipClass = chip
